import { useState, useEffect } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Loader2, BarChart3, Users, DollarSign, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function AdminReports() {
  const [tab, setTab] = useState('platform');
  const [days, setDays] = useState('30');
  const [platformData, setPlatformData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userList, setUserList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    adminApi.reportPlatform({ days: parseInt(days) }).then(r => setPlatformData(r.data)).catch(() => {});
  }, [days]);

  const searchUsers = async () => {
    if (!userSearch) return;
    const res = await adminApi.users({ search: userSearch, limit: 10 });
    setUserList(res.data.users || []);
  };

  const loadUserReport = async (userId) => {
    setLoading(true);
    setSelectedUser(userId);
    try {
      const res = await adminApi.reportUser(userId, { days: parseInt(days) });
      setUserData(res.data);
    } catch {}
    setLoading(false);
  };

  const tooltipStyle = { contentStyle: { background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' } };

  // Aggregate daily data for chart
  const dailyChart = {};
  (platformData?.daily || []).forEach(d => {
    if (!dailyChart[d.date]) dailyChart[d.date] = { date: d.date, requests: 0 };
    dailyChart[d.date].requests += d.requests;
  });
  const chartData = Object.values(dailyChart).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div data-testid="admin-reports" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Usage Reports</h2>
          <p className="text-muted-foreground mt-1">API usage by service, user, and day</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="platform">Platform Report</TabsTrigger>
          <TabsTrigger value="user">User Report</TabsTrigger>
        </TabsList>

        {/* Platform Report */}
        <TabsContent value="platform" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-card border-border/50"><CardContent className="p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total GPU Requests</p>
              <p className="text-3xl font-bold mt-1">{platformData?.total_requests?.toLocaleString() || 0}</p>
            </CardContent></Card>
            <Card className="bg-card border-border/50"><CardContent className="p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Users</p>
              <p className="text-3xl font-bold mt-1">{platformData?.active_users || 0}</p>
            </CardContent></Card>
            <Card className="bg-card border-border/50"><CardContent className="p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Est. GPU Cost</p>
              <p className="text-3xl font-bold mt-1">${platformData?.total_est_cost?.toFixed(2) || '0.00'}</p>
            </CardContent></Card>
          </div>

          {chartData.length > 0 && (
            <Card className="bg-card border-border/50">
              <CardHeader><CardTitle className="text-lg">Daily GPU Requests</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip {...tooltipStyle} />
                    <Bar dataKey="requests" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-lg">By Service</CardTitle></CardHeader>
            <CardContent>
              {!(platformData?.by_service?.length) ? <p className="text-sm text-muted-foreground py-4 text-center">No usage data</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border/50 text-muted-foreground"><th className="text-left py-2 px-3">Service</th><th className="text-right py-2 px-3">Requests</th><th className="text-right py-2 px-3">Avg Latency</th><th className="text-right py-2 px-3">Hours</th><th className="text-right py-2 px-3">Users</th><th className="text-right py-2 px-3">Est. Cost</th></tr></thead>
                    <tbody>
                      {platformData.by_service.map((s, i) => (
                        <tr key={i} className="border-b border-border/20">
                          <td className="py-2 px-3 font-medium">{s.service}</td>
                          <td className="py-2 px-3 text-right font-mono">{s.requests.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right font-mono">{s.avg_latency_ms}ms</td>
                          <td className="py-2 px-3 text-right font-mono">{s.hours}</td>
                          <td className="py-2 px-3 text-right font-mono">{s.active_users}</td>
                          <td className="py-2 px-3 text-right font-mono">${s.est_cost.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Report */}
        <TabsContent value="user" className="space-y-4 mt-4">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search user by email..." value={userSearch} onChange={e => setUserSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchUsers()} className="pl-9" />
                </div>
                <Button onClick={searchUsers} variant="outline">Search</Button>
              </div>
              {userList.length > 0 && (
                <div className="mt-2 space-y-1">
                  {userList.map(u => (
                    <button key={u._id} onClick={() => loadUserReport(u._id)} className={`w-full flex items-center justify-between p-2 rounded-lg text-sm hover:bg-secondary/20 transition-colors ${selectedUser === u._id ? 'bg-primary/10' : ''}`}>
                      <span className="font-mono text-xs">{u.email}</span>
                      <span className="text-xs text-muted-foreground">{u.full_name} | {u.plan}</span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

          {userData && !loading && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-card border-border/50"><CardContent className="p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Requests</p>
                  <p className="text-3xl font-bold mt-1">{userData.total_requests?.toLocaleString() || 0}</p>
                </CardContent></Card>
                <Card className="bg-card border-border/50"><CardContent className="p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Cost</p>
                  <p className="text-3xl font-bold mt-1">${userData.total_cost?.toFixed(2) || '0.00'}</p>
                </CardContent></Card>
              </div>

              <Card className="bg-card border-border/50">
                <CardHeader><CardTitle className="text-lg">Usage by Service</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-border/50 text-muted-foreground"><th className="text-left py-2 px-3">Service</th><th className="text-left py-2 px-3">Tier</th><th className="text-right py-2 px-3">Requests</th><th className="text-right py-2 px-3">Avg Latency</th><th className="text-right py-2 px-3">Hours</th><th className="text-right py-2 px-3">Cost</th></tr></thead>
                      <tbody>
                        {(userData.by_service || []).map((s, i) => (
                          <tr key={i} className="border-b border-border/20">
                            <td className="py-2 px-3 font-medium">{s.service}</td>
                            <td className="py-2 px-3"><Badge variant="outline" className="text-xs">{s.tier}</Badge></td>
                            <td className="py-2 px-3 text-right font-mono">{s.requests.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right font-mono">{s.avg_latency_ms}ms</td>
                            <td className="py-2 px-3 text-right font-mono">{s.hours}</td>
                            <td className="py-2 px-3 text-right font-mono">${s.cost.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
